import { Controller } from "@hotwired/stimulus";
import { cardDetector } from "../utils/card-detection.js";
import PerspT from "perspective-transform";

// Initialize card detector early
cardDetector.initialize().then(() => {
  console.log('Card detector initialized');
});

export default class extends Controller {
  static targets = [
    "modal",
    "canvasContainer",
    "canvas",
    "submitButton",
    "resetButton",
    "autoDetectButton"
  ];

  static values = {
    imageUrl: String,
    fileName: String,
  };

  connect() {
    console.log("Perspective crop controller connected!");
    this.canvas = null;
    this.ctx = null;
    this.image = null;
    this.points = [];
    this.dragging = false;
    this.dragPointIndex = -1;
    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.debugMode = false; // Enable debug visualization
    this.draggingEdge = -1; // Index of edge being dragged (-1 = none, 0-3 = edge index)
    this.edgeHoverIndex = -1; // Index of edge being hovered
    
    this.boundShowModal = this.handleShowModal.bind(this);
    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundResize = this.handleResize.bind(this);
    this.resizeTimeout = null;
    
    document.addEventListener("show-perspective-crop", this.boundShowModal);
    window.addEventListener("resize", this.boundResize);
    console.log("Perspective crop controller setup complete");
  }

  disconnect() {
    document.removeEventListener("show-perspective-crop", this.boundShowModal);
    window.removeEventListener("resize", this.boundResize);
    this.removeCanvasListeners();
  }

  handleShowModal(event) {
    console.log("Perspective crop modal show event received:", event.detail);
    const { imageUrl, fileName, suggestedCorners } = event.detail;
    this.suggestedCorners = suggestedCorners || null;
    this.show(imageUrl, fileName);
  }

  show(imageUrl, fileName) {
    console.log("Showing perspective crop modal with:", { imageUrl: imageUrl.substring(0, 50) + "...", fileName });
    this.imageUrlValue = imageUrl;
    this.fileNameValue = fileName;
    // Use flex so centering works via CSS
    this.modalTarget.style.display = "flex";
    document.body.style.overflow = "hidden";
    
    // Add mobile-specific class for styling
    if (window.innerWidth <= 768) {
      this.modalTarget.classList.add('perspective-crop-modal--mobile');
    } else {
      this.modalTarget.classList.remove('perspective-crop-modal--mobile');
    }
    
    this.initializeCanvas();
  }

  hide() {
    this.modalTarget.style.display = "none";
    document.body.style.overflow = "";
    this.removeCanvasListeners();
  }

  initializeCanvas() {
    if (!this.canvasTarget) return;

    this.canvas = this.canvasTarget;
    this.ctx = this.canvas.getContext("2d");
    
    // Set canvas size - use full container on mobile
    const container = this.canvasContainerTarget;
    const containerRect = container.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile, use full container size
      this.canvas.width = containerRect.width - 20;
      this.canvas.height = containerRect.height - 20;
    } else {
      // On desktop, leave room for padding
      this.canvas.width = containerRect.width - 40;
      this.canvas.height = containerRect.height - 40;
    }

    this.loadImage();
    this.addCanvasListeners();
  }

  loadImage() {
    this.image = new Image();
    this.image.onload = async () => {
      console.log('Image loaded:', this.image.width, 'x', this.image.height);
      this.fitImageToCanvas();
      this.initializeDefaultPoints();
      this.draw();
      this.enableControls();
      
      // Try auto-detect immediately after loading with slight delay for canvas setup
      setTimeout(async () => {
        // Server-side OpenCV corner suggestions beat the in-browser heuristics
        if (this.applySuggestedCorners()) {
          console.log('Applied server-suggested corners');
          return;
        }
        console.log('Starting automatic edge detection');
        await this.autoDetect();
      }, 100);
    };
    this.image.src = this.imageUrlValue;
  }

  fitImageToCanvas() {
    const canvasAspect = this.canvas.width / this.canvas.height;
    const imageAspect = this.image.width / this.image.height;

    if (imageAspect > canvasAspect) {
      // Image is wider than canvas
      this.scale = this.canvas.width / this.image.width;
    } else {
      // Image is taller than canvas
      this.scale = this.canvas.height / this.image.height;
    }

    const scaledWidth = this.image.width * this.scale;
    const scaledHeight = this.image.height * this.scale;

    this.offsetX = (this.canvas.width - scaledWidth) / 2;
    this.offsetY = (this.canvas.height - scaledHeight) / 2;
    
    console.log('Canvas setup - Scale:', this.scale, 'Offset:', this.offsetX, this.offsetY);
    console.log('Scaled image size:', scaledWidth, 'x', scaledHeight);
  }

  initializeDefaultPoints() {
    const margin = 50;
    const imageWidth = this.image.width * this.scale;
    const imageHeight = this.image.height * this.scale;

    // Order: top-left, top-right, bottom-right, bottom-left
    this.points = [
      { x: this.offsetX + margin, y: this.offsetY + margin }, // Top-left
      { x: this.offsetX + imageWidth - margin, y: this.offsetY + margin }, // Top-right
      { x: this.offsetX + imageWidth - margin, y: this.offsetY + imageHeight - margin }, // Bottom-right
      { x: this.offsetX + margin, y: this.offsetY + imageHeight - margin } // Bottom-left
    ];
    
    console.log("Initialized default points:", this.points);
  }

  addCanvasListeners() {
    this.canvas.addEventListener("mousedown", this.boundMouseDown);
    this.canvas.addEventListener("mousemove", this.boundMouseMove);
    this.canvas.addEventListener("mouseup", this.boundMouseUp);
    
    // Touch events for mobile
    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this));
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this));
    this.canvas.addEventListener("touchend", this.handleTouchEnd.bind(this));
  }

  removeCanvasListeners() {
    if (this.canvas) {
      this.canvas.removeEventListener("mousedown", this.boundMouseDown);
      this.canvas.removeEventListener("mousemove", this.boundMouseMove);
      this.canvas.removeEventListener("mouseup", this.boundMouseUp);
    }
  }

  handleMouseDown(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const isMobile = window.innerWidth <= 768;

    // Check if clicking near any point
    for (let i = 0; i < this.points.length; i++) {
      const distance = Math.sqrt(
        Math.pow(x - this.points[i].x, 2) + Math.pow(y - this.points[i].y, 2)
      );
      // Larger touch target on mobile
      const threshold = isMobile ? 35 : 20;
      if (distance < threshold) {
        this.dragging = true;
        this.dragPointIndex = i;
        this.draggingEdge = -1;
        this.canvas.style.cursor = "grabbing";
        return;
      }
    }
    
    // Check if clicking near any edge
    for (let i = 0; i < 4; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % 4];
      const distance = this.distanceToLine(x, y, p1.x, p1.y, p2.x, p2.y);
      
      // Larger touch target on mobile
      const edgeThreshold = isMobile ? 25 : 10;
      if (distance < edgeThreshold) {
        this.dragging = true;
        this.draggingEdge = i;
        this.dragPointIndex = -1;
        this.canvas.style.cursor = "grabbing";
        break;
      }
    }
  }

  handleMouseMove(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Store mouse position for hover effects
    this.lastMouseX = x;
    this.lastMouseY = y;

    if (this.dragging) {
      if (this.dragPointIndex !== -1) {
        // Dragging a corner point
        this.points[this.dragPointIndex] = { x, y };
        this.draw();
      } else if (this.draggingEdge !== -1) {
        // Dragging an edge
        const p1Index = this.draggingEdge;
        const p2Index = (this.draggingEdge + 1) % 4;
        
        // Calculate the movement vector
        const p1 = this.points[p1Index];
        const p2 = this.points[p2Index];
        
        // Store original positions if this is the start of drag
        if (!this.edgeDragStart) {
          this.edgeDragStart = {
            p1: { ...p1 },
            p2: { ...p2 },
            mouseX: x,
            mouseY: y
          };
        }
        
        // Calculate movement from drag start
        const deltaX = x - this.edgeDragStart.mouseX;
        const deltaY = y - this.edgeDragStart.mouseY;
        
        // Project movement onto perpendicular to edge (using original positions)
        const edgeVector = { 
          x: this.edgeDragStart.p2.x - this.edgeDragStart.p1.x, 
          y: this.edgeDragStart.p2.y - this.edgeDragStart.p1.y 
        };
        const perpVector = { x: -edgeVector.y, y: edgeVector.x };
        const perpLength = Math.sqrt(perpVector.x * perpVector.x + perpVector.y * perpVector.y);
        
        if (perpLength > 0) {
          perpVector.x /= perpLength;
          perpVector.y /= perpLength;
          
          // Calculate projected movement distance
          const moveDistance = perpVector.x * deltaX + perpVector.y * deltaY;
          
          // Move both points of the edge
          this.points[p1Index] = {
            x: this.edgeDragStart.p1.x + perpVector.x * moveDistance,
            y: this.edgeDragStart.p1.y + perpVector.y * moveDistance
          };
          this.points[p2Index] = {
            x: this.edgeDragStart.p2.x + perpVector.x * moveDistance,
            y: this.edgeDragStart.p2.y + perpVector.y * moveDistance
          };
        }
        
        this.draw();
      }
    } else {
      // Check if hovering over a point
      let hoveringPoint = false;
      for (let i = 0; i < this.points.length; i++) {
        const distance = Math.sqrt(
          Math.pow(x - this.points[i].x, 2) + Math.pow(y - this.points[i].y, 2)
        );
        if (distance < 20) {
          hoveringPoint = true;
          this.edgeHoverIndex = -1;
          break;
        }
      }
      
      // If not hovering over a point, check edges
      if (!hoveringPoint) {
        let hoveringEdge = false;
        for (let i = 0; i < 4; i++) {
          const p1 = this.points[i];
          const p2 = this.points[(i + 1) % 4];
          const distance = this.distanceToLine(x, y, p1.x, p1.y, p2.x, p2.y);
          
          if (distance < 10) {
            hoveringEdge = true;
            this.edgeHoverIndex = i;
            break;
          }
        }
        
        if (!hoveringEdge) {
          this.edgeHoverIndex = -1;
        }
        
        this.canvas.style.cursor = hoveringEdge ? "move" : "default";
      } else {
        this.canvas.style.cursor = "grab";
      }
      
      // Redraw to show hover state
      this.draw();
    }
  }

  handleMouseUp() {
    this.dragging = false;
    this.dragPointIndex = -1;
    this.draggingEdge = -1;
    this.edgeDragStart = null;
    this.canvas.style.cursor = "default";
    this.draw(); // Redraw to update visual state
  }

  // Touch event handlers
  handleTouchStart(event) {
    event.preventDefault();
    const touch = event.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    // Check if touching a corner point
    for (let i = 0; i < this.points.length; i++) {
      const distance = Math.sqrt(
        Math.pow(x - this.points[i].x, 2) + Math.pow(y - this.points[i].y, 2)
      );
      // Larger touch target for mobile
      if (distance < 40) {
        this.dragging = true;
        this.dragPointIndex = i;
        this.draggingEdge = -1;
        return;
      }
    }
    
    // Check if touching an edge
    for (let i = 0; i < 4; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % 4];
      const distance = this.distanceToLine(x, y, p1.x, p1.y, p2.x, p2.y);
      
      // Larger touch target for mobile
      if (distance < 30) {
        this.dragging = true;
        this.draggingEdge = i;
        this.dragPointIndex = -1;
        this.edgeDragStart = {
          p1: { ...p1 },
          p2: { ...p2 },
          mouseX: x,
          mouseY: y
        };
        break;
      }
    }
  }

  handleTouchMove(event) {
    event.preventDefault();
    const touch = event.touches[0];
    const rect = this.canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    if (this.dragging) {
      if (this.dragPointIndex !== -1) {
        // Dragging a corner point
        this.points[this.dragPointIndex] = { x, y };
        this.draw();
      } else if (this.draggingEdge !== -1) {
        // Dragging an edge
        const p1Index = this.draggingEdge;
        const p2Index = (this.draggingEdge + 1) % 4;
        
        // Calculate movement from drag start
        const deltaX = x - this.edgeDragStart.mouseX;
        const deltaY = y - this.edgeDragStart.mouseY;
        
        // Project movement onto perpendicular to edge
        const edgeVector = { 
          x: this.edgeDragStart.p2.x - this.edgeDragStart.p1.x, 
          y: this.edgeDragStart.p2.y - this.edgeDragStart.p1.y 
        };
        const perpVector = { x: -edgeVector.y, y: edgeVector.x };
        const perpLength = Math.sqrt(perpVector.x * perpVector.x + perpVector.y * perpVector.y);
        
        if (perpLength > 0) {
          perpVector.x /= perpLength;
          perpVector.y /= perpLength;
          
          // Calculate projected movement distance
          const moveDistance = perpVector.x * deltaX + perpVector.y * deltaY;
          
          // Move both points of the edge
          this.points[p1Index] = {
            x: this.edgeDragStart.p1.x + perpVector.x * moveDistance,
            y: this.edgeDragStart.p1.y + perpVector.y * moveDistance
          };
          this.points[p2Index] = {
            x: this.edgeDragStart.p2.x + perpVector.x * moveDistance,
            y: this.edgeDragStart.p2.y + perpVector.y * moveDistance
          };
        }
        
        this.draw();
      }
    }
  }

  handleTouchEnd(event) {
    event.preventDefault();
    this.handleMouseUp();
  }

  draw() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw image
    this.ctx.drawImage(
      this.image,
      this.offsetX,
      this.offsetY,
      this.image.width * this.scale,
      this.image.height * this.scale
    );

    // Draw overlay
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw selected area (clear the overlay)
    this.ctx.globalCompositeOperation = "destination-out";
    this.ctx.beginPath();
    this.ctx.moveTo(this.points[0].x, this.points[0].y);
    for (let i = 1; i < this.points.length; i++) {
      this.ctx.lineTo(this.points[i].x, this.points[i].y);
    }
    this.ctx.closePath();
    this.ctx.fill();

    // Reset composite operation
    this.ctx.globalCompositeOperation = "source-over";

    // Draw selection border
    // Draw the quadrilateral edges with hover effect
    for (let i = 0; i < 4; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % 4];
      
      this.ctx.beginPath();
      this.ctx.moveTo(p1.x, p1.y);
      this.ctx.lineTo(p2.x, p2.y);
      
      // Highlight hovered or dragged edge
      if (this.edgeHoverIndex === i || this.draggingEdge === i) {
        // Draw a thicker, highlighted edge
        this.ctx.strokeStyle = "#60a5fa";
        this.ctx.lineWidth = 4;
        this.ctx.setLineDash([]);
      } else {
        this.ctx.strokeStyle = "#3b82f6";
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([]);
      }
      
      this.ctx.stroke();
      
      // Add a subtle shadow for depth
      if (this.edgeHoverIndex === i || this.draggingEdge === i) {
        this.ctx.shadowColor = "rgba(59, 130, 246, 0.5)";
        this.ctx.shadowBlur = 10;
        this.ctx.stroke();
        this.ctx.shadowBlur = 0;
      }
    }
    
    // Draw grip indicators on edges when hovering
    if (this.edgeHoverIndex >= 0) {
      const p1 = this.points[this.edgeHoverIndex];
      const p2 = this.points[(this.edgeHoverIndex + 1) % 4];
      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;
      
      // Draw grip handle
      this.ctx.fillStyle = "#60a5fa";
      this.ctx.strokeStyle = "#ffffff";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(midX, midY, 6, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.stroke();
    }

    // Draw corner points with better labels
    this.ctx.fillStyle = "#3b82f6";
    this.ctx.strokeStyle = "#ffffff";
    this.ctx.lineWidth = 2;
    
    const labels = ["TL", "TR", "BR", "BL"]; // Top-Left, Top-Right, Bottom-Right, Bottom-Left
    
    for (let i = 0; i < this.points.length; i++) {
      this.ctx.beginPath();
      this.ctx.arc(this.points[i].x, this.points[i].y, 10, 0, 2 * Math.PI);
      this.ctx.fill();
      this.ctx.stroke();

      // Draw point labels
      this.ctx.fillStyle = "#ffffff";
      this.ctx.font = "bold 10px Arial";
      this.ctx.textAlign = "center";
      this.ctx.fillText(labels[i], this.points[i].x, this.points[i].y + 3);
      this.ctx.fillStyle = "#3b82f6";
    }
  }

  toggleDebugMode() {
    this.debugMode = !this.debugMode;
    console.log('Debug mode:', this.debugMode ? 'ON' : 'OFF');
    this.draw();
  }

  // Seed corner points from the server's classical OpenCV detection, passed
  // through the show-perspective-crop event. Coordinates arrive in original
  // image pixel space as [[x, y] * 4] ordered tl/tr/br/bl.
  applySuggestedCorners() {
    const suggestion = this.suggestedCorners;
    if (!suggestion || !Array.isArray(suggestion.corners) || suggestion.corners.length !== 4) {
      return false;
    }

    // Rescale in case the server saw different dimensions than the loaded image
    const scaleX = suggestion.imageWidth ? this.image.width / suggestion.imageWidth : 1;
    const scaleY = suggestion.imageHeight ? this.image.height / suggestion.imageHeight : 1;
    const corners = suggestion.corners.map(([x, y]) => ({ x: x * scaleX, y: y * scaleY }));

    const validCorners = this.validateDetectedCorners(corners);
    if (!validCorners) {
      console.warn('Server-suggested corners failed validation, falling back to local detection');
      return false;
    }

    this.points = validCorners.map((corner) => ({
      x: this.offsetX + corner.x * this.scale,
      y: this.offsetY + corner.y * this.scale,
    }));
    this.draw();
    this.showSuccessMessage("Card edges detected! Adjust corners if needed.");
    return true;
  }

  async autoDetect() {
    console.log('Auto-detect triggered');
    this.showLoadingState("Analyzing image for business card edges...");
    
    try {
      // Ensure card detector is initialized
      await cardDetector.initialize();
      
      // Use computer vision to detect card corners
      console.log('Detecting corners for image:', this.image.width, 'x', this.image.height);
      const detectedCorners = await cardDetector.suggestCorners(this.image);
      
      if (detectedCorners && detectedCorners.length === 4) {
        console.log('Raw detected corners (image coordinates):', detectedCorners);
        
        // Validate that corners make sense
        const validCorners = this.validateDetectedCorners(detectedCorners);
        
        if (!validCorners) {
          console.warn('Detected corners failed validation, using fallback');
          throw new Error("Detected corners invalid");
        }
        
        // Convert image coordinates to screen coordinates
        this.points = validCorners.map((corner, index) => {
          const screenX = this.offsetX + (corner.x * this.scale);
          const screenY = this.offsetY + (corner.y * this.scale);
          console.log(`Corner ${index}: Image(${corner.x.toFixed(1)}, ${corner.y.toFixed(1)}) -> Screen(${screenX.toFixed(1)}, ${screenY.toFixed(1)})`);
          return { x: screenX, y: screenY };
        });
        
        this.draw();
        
        // Show a brief success message
        this.showSuccessMessage("Card edges detected! Adjust corners if needed.");
      } else {
        console.log('No corners detected, using intelligent fallback');
        throw new Error("Could not detect card edges automatically");
      }
    } catch (error) {
      console.warn("Auto-detection failed, using intelligent fallback:", error);
      
      // Use intelligent fallback based on image analysis
      try {
        // Create a canvas to analyze the image
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = this.image.width;
        tempCanvas.height = this.image.height;
        tempCtx.drawImage(this.image, 0, 0);
        
        // Try to find bright regions (likely business card)
        const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imageData.data;
        
        let minX = this.image.width, maxX = 0;
        let minY = this.image.height, maxY = 0;
        let foundCard = false;
        
        // Sample the image to find bright regions
        const sampleStep = Math.max(1, Math.floor(Math.min(this.image.width, this.image.height) / 100));
        
        for (let y = 0; y < this.image.height; y += sampleStep) {
          for (let x = 0; x < this.image.width; x += sampleStep) {
            const idx = (y * this.image.width + x) * 4;
            const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
            
            // If pixel is bright (likely part of business card)
            if (brightness > 180) {
              minX = Math.min(minX, x);
              maxX = Math.max(maxX, x);
              minY = Math.min(minY, y);
              maxY = Math.max(maxY, y);
              foundCard = true;
            }
          }
        }
        
        if (foundCard && maxX > minX + 50 && maxY > minY + 50) {
          // Add some padding
          const padding = 20;
          minX = Math.max(0, minX - padding);
          maxX = Math.min(this.image.width, maxX + padding);
          minY = Math.max(0, minY - padding);
          maxY = Math.min(this.image.height, maxY + padding);
          
          // Convert to screen coordinates
          this.points = [
            { x: this.offsetX + (minX * this.scale), y: this.offsetY + (minY * this.scale) },
            { x: this.offsetX + (maxX * this.scale), y: this.offsetY + (minY * this.scale) },
            { x: this.offsetX + (maxX * this.scale), y: this.offsetY + (maxY * this.scale) },
            { x: this.offsetX + (minX * this.scale), y: this.offsetY + (maxY * this.scale) }
          ];
          
          console.log('Used intelligent bounds detection');
        } else {
          // Final fallback to centered rectangle
          const margin = 30;
          const imageWidth = this.image.width * this.scale;
          const imageHeight = this.image.height * this.scale;
          const aspectRatio = 1.75; // Standard business card aspect ratio

          let cardWidth, cardHeight;
          if (imageWidth / imageHeight > aspectRatio) {
            // Image is wider
            cardHeight = imageHeight - (margin * 2);
            cardWidth = cardHeight * aspectRatio;
          } else {
            // Image is taller
            cardWidth = imageWidth - (margin * 2);
            cardHeight = cardWidth / aspectRatio;
          }

          const centerX = this.offsetX + (imageWidth / 2);
          const centerY = this.offsetY + (imageHeight / 2);

          this.points = [
            { x: centerX - cardWidth / 2, y: centerY - cardHeight / 2 },
            { x: centerX + cardWidth / 2, y: centerY - cardHeight / 2 },
            { x: centerX + cardWidth / 2, y: centerY + cardHeight / 2 },
            { x: centerX - cardWidth / 2, y: centerY + cardHeight / 2 }
          ];
          
          console.log('Used standard fallback rectangle');
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        // Use default points as last resort
        this.initializeDefaultPoints();
      }

      this.draw();
      this.showSuccessMessage("Please adjust the corners to match your business card edges.");
    } finally {
      this.hideLoadingState();
    }
  }
  
  validateDetectedCorners(corners) {
    if (!corners || corners.length !== 4) {
      console.log('Invalid corner count:', corners ? corners.length : 0);
      return null;
    }
    
    // Check each corner is within image bounds
    for (let i = 0; i < corners.length; i++) {
      const corner = corners[i];
      if (corner.x < -10 || corner.x > this.image.width + 10 ||
          corner.y < -10 || corner.y > this.image.height + 10) {
        console.log(`Corner ${i} out of bounds:`, corner);
        return null;
      }
    }
    
    // Calculate bounds
    const minX = Math.min(...corners.map(c => c.x));
    const maxX = Math.max(...corners.map(c => c.x));
    const minY = Math.min(...corners.map(c => c.y));
    const maxY = Math.max(...corners.map(c => c.y));
    
    const detectedWidth = maxX - minX;
    const detectedHeight = maxY - minY;
    
    console.log('Detected card bounds:', minX.toFixed(1), minY.toFixed(1), 'to', maxX.toFixed(1), maxY.toFixed(1));
    console.log('Detected card size:', detectedWidth.toFixed(1), 'x', detectedHeight.toFixed(1));
    
    // Check if detection is reasonable (not too small)
    if (detectedWidth < this.image.width * 0.1 || detectedHeight < this.image.height * 0.1) {
      console.warn('Detected region too small');
      return null;
    }
    
    // Check if detection is reasonable (not entire image)
    if (detectedWidth > this.image.width * 0.98 && detectedHeight > this.image.height * 0.98) {
      console.warn('Detected entire image instead of card');
      return null;
    }
    
    // Check aspect ratio
    const aspectRatio = detectedWidth / detectedHeight;
    const isLandscape = aspectRatio > 1.3 && aspectRatio < 2.3;
    const isPortrait = aspectRatio > 0.43 && aspectRatio < 0.77;
    
    if (!isLandscape && !isPortrait) {
      console.warn('Detected region has invalid aspect ratio:', aspectRatio.toFixed(2));
      return null;
    }
    
    // Ensure corners are ordered correctly (top-left, top-right, bottom-right, bottom-left)
    const orderedCorners = this.orderDetectedCorners(corners);
    console.log('Validated and ordered corners:', orderedCorners);
    
    return orderedCorners;
  }
  
  orderDetectedCorners(corners) {
    // Sort by y coordinate to separate top from bottom
    const sortedByY = [...corners].sort((a, b) => a.y - b.y);
    
    // Top two points
    const topPoints = sortedByY.slice(0, 2).sort((a, b) => a.x - b.x);
    // Bottom two points  
    const bottomPoints = sortedByY.slice(2, 4).sort((a, b) => a.x - b.x);
    
    return [
      topPoints[0],    // top-left
      topPoints[1],    // top-right
      bottomPoints[1], // bottom-right
      bottomPoints[0]  // bottom-left
    ];
  }
  
  distanceToLine(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) {
      param = dot / lenSq;
    }
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  }

  reset() {
    this.initializeDefaultPoints();
    this.draw();
  }

  async crop() {
    this.disableControls();
    this.submitButtonTarget.textContent = "Processing...";

    try {
      // Calculate optimal output size based on input image and selection
      const sourceWidth = this.image.width;
      const sourceHeight = this.image.height;
      
      // Calculate the size of the selected area
      const minX = Math.min(...this.points.map(p => (p.x - this.offsetX) / this.scale));
      const maxX = Math.max(...this.points.map(p => (p.x - this.offsetX) / this.scale));
      const minY = Math.min(...this.points.map(p => (p.y - this.offsetY) / this.scale));
      const maxY = Math.max(...this.points.map(p => (p.y - this.offsetY) / this.scale));
      
      const selectionWidth = maxX - minX;
      const selectionHeight = maxY - minY;
      
      // Use higher resolution but respect the original image quality
      const maxOutputWidth = Math.min(1600, sourceWidth * 1.5);
      const aspectRatio = 1.75; // Business card aspect ratio
      
      let outputWidth, outputHeight;
      if (selectionWidth / selectionHeight > aspectRatio) {
        outputHeight = Math.min(maxOutputWidth / aspectRatio, selectionHeight * 2);
        outputWidth = outputHeight * aspectRatio;
      } else {
        outputWidth = Math.min(maxOutputWidth, selectionWidth * 2);
        outputHeight = outputWidth / aspectRatio;
      }
      
      outputWidth = Math.round(outputWidth);
      outputHeight = Math.round(outputHeight);
      
      console.log(`Output dimensions: ${outputWidth}x${outputHeight}`);
      
      const outputCanvas = document.createElement("canvas");
      outputCanvas.width = outputWidth;
      outputCanvas.height = outputHeight;
      const outputCtx = outputCanvas.getContext("2d");
      
      // Enable image smoothing for better quality
      outputCtx.imageSmoothingEnabled = true;
      outputCtx.imageSmoothingQuality = "high";

      // Convert screen coordinates to image coordinates
      const imagePoints = this.points.map(point => ({
        x: (point.x - this.offsetX) / this.scale,
        y: (point.y - this.offsetY) / this.scale
      }));

      // Apply perspective transformation
      this.applyPerspectiveTransform(
        this.image,
        imagePoints,
        outputCanvas,
        outputCtx,
        outputWidth,
        outputHeight
      );

      // Convert to blob
      const blob = await new Promise(resolve => {
        outputCanvas.toBlob(resolve, "image/jpeg", 0.9);
      });

      // Create file
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      // Remove existing extension from filename to avoid double extensions
      const baseFileName = this.fileNameValue ? this.fileNameValue.replace(/\.[^/.]+$/, "") : timestamp;
      const croppedFile = new File(
        [blob],
        `perspective-corrected-${baseFileName}.jpg`,
        { type: "image/jpeg" }
      );

      // Dispatch event
      const uploadContainer = document.querySelector('[data-controller="upload"]');
      if (uploadContainer) {
        const event = new CustomEvent("perspective-crop:cropped", {
          detail: {
            file: croppedFile,
            originalFileName: this.fileNameValue,
          },
          bubbles: true
        });
        uploadContainer.dispatchEvent(event);
      }

      this.hide();
    } catch (error) {
      console.error("Error applying perspective correction:", error);
      alert("Failed to process image. Please try again.");
      this.enableControls();
      this.submitButtonTarget.textContent = "Apply Correction";
    }
  }

  applyPerspectiveTransform(sourceImage, sourcePoints, targetCanvas, targetCtx, targetWidth, targetHeight) {
    console.log("Applying perspective transform with points:", sourcePoints);
    
    // Clear the target canvas
    targetCtx.clearRect(0, 0, targetWidth, targetHeight);

    // Create source canvas for pixel sampling
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d');
    sourceCanvas.width = sourceImage.width;
    sourceCanvas.height = sourceImage.height;
    sourceCtx.drawImage(sourceImage, 0, 0);
    const sourceImageData = sourceCtx.getImageData(0, 0, sourceImage.width, sourceImage.height);

    // Create target image data
    const imageData = targetCtx.createImageData(targetWidth, targetHeight);

    console.log("Source points (image coordinates):", sourcePoints);
    console.log("Target dimensions:", targetWidth, "x", targetHeight);
    
    // Ensure points are in correct order: top-left, top-right, bottom-right, bottom-left
    const orderedPoints = this.ensureCorrectPointOrder(sourcePoints);
    console.log("Ordered source points:", orderedPoints);

    try {
      // Use flat array format [x1,y1,x2,y2,x3,y3,x4,y4] - this is the correct format for perspective-transform
      const srcQuad = [
        orderedPoints[0].x, orderedPoints[0].y,  // top-left
        orderedPoints[1].x, orderedPoints[1].y,  // top-right
        orderedPoints[2].x, orderedPoints[2].y,  // bottom-right
        orderedPoints[3].x, orderedPoints[3].y   // bottom-left
      ];

      const dstQuad = [
        0, 0,                     // top-left
        targetWidth, 0,           // top-right
        targetWidth, targetHeight, // bottom-right
        0, targetHeight           // bottom-left
      ];

      console.log("Source quad for transform:", srcQuad);
      console.log("Destination quad for transform:", dstQuad);

      const perspT = PerspT(srcQuad, dstQuad);
      console.log("Perspective transform created successfully");

      // Test a few points to verify transformation
      const cornerTests = [
        [0, 0], [targetWidth-1, 0], 
        [targetWidth-1, targetHeight-1], [0, targetHeight-1]
      ];
      
      cornerTests.forEach(([x, y], i) => {
        const sourceCoord = perspT.transformInverse(x, y);
        console.log(`Corner ${i}: Target (${x}, ${y}) -> Source (${sourceCoord[0].toFixed(1)}, ${sourceCoord[1].toFixed(1)})`);
      });

      // Transform each pixel
      let pixelsProcessed = 0;
      let pixelsSkipped = 0;
      
      for (let y = 0; y < targetHeight; y++) {
        for (let x = 0; x < targetWidth; x++) {
          try {
            const sourceCoord = perspT.transformInverse(x, y);
            
            if (sourceCoord && sourceCoord.length >= 2 &&
                sourceCoord[0] >= 0 && sourceCoord[0] < sourceImage.width &&
                sourceCoord[1] >= 0 && sourceCoord[1] < sourceImage.height) {
              
              const color = this.samplePixelBilinear(sourceImageData, sourceCoord[0], sourceCoord[1]);
              
              const targetIndex = (y * targetWidth + x) * 4;
              imageData.data[targetIndex] = color.r;
              imageData.data[targetIndex + 1] = color.g;
              imageData.data[targetIndex + 2] = color.b;
              imageData.data[targetIndex + 3] = 255;
              
              pixelsProcessed++;
            } else {
              pixelsSkipped++;
            }
          } catch (pixelError) {
            pixelsSkipped++;
          }
        }
      }
      
      console.log(`Transformation complete: ${pixelsProcessed} processed, ${pixelsSkipped} skipped`);
      targetCtx.putImageData(imageData, 0, 0);
      console.log("Perspective transformation completed successfully");
      
    } catch (error) {
      console.error("Perspective transformation failed:", error);
      console.error("Error details:", error.message);
      // Fallback to simple bilinear interpolation
      this.applyFallbackTransform(sourceImage, orderedPoints || sourcePoints, targetCanvas, targetCtx, targetWidth, targetHeight, imageData, sourceImageData);
    }
  }

  ensureCorrectPointOrder(points) {
    // Ensure points are ordered as: top-left, top-right, bottom-right, bottom-left
    if (points.length !== 4) return points;
    
    // Calculate center point
    const centerX = points.reduce((sum, p) => sum + p.x, 0) / 4;
    const centerY = points.reduce((sum, p) => sum + p.y, 0) / 4;
    
    // Sort points by angle from center to ensure consistent ordering
    const sortedPoints = [...points].map(point => {
      const angle = Math.atan2(point.y - centerY, point.x - centerX);
      return { ...point, angle };
    }).sort((a, b) => a.angle - b.angle);
    
    // Find top-left (smallest x+y sum)
    let topLeftIndex = 0;
    let minSum = sortedPoints[0].x + sortedPoints[0].y;
    
    for (let i = 1; i < sortedPoints.length; i++) {
      const sum = sortedPoints[i].x + sortedPoints[i].y;
      if (sum < minSum) {
        minSum = sum;
        topLeftIndex = i;
      }
    }
    
    // Reorder starting from top-left going clockwise
    const ordered = [];
    for (let i = 0; i < 4; i++) {
      const index = (topLeftIndex + i) % 4;
      ordered.push({ x: sortedPoints[index].x, y: sortedPoints[index].y });
    }
    
    console.log("Point ordering - Original:", points.map(p => `(${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));
    console.log("Point ordering - Corrected:", ordered.map(p => `(${p.x.toFixed(1)}, ${p.y.toFixed(1)})`));
    
    return ordered;
  }

  applyFallbackTransform(sourceImage, sourcePoints, targetCanvas, targetCtx, targetWidth, targetHeight, imageData, sourceImageData) {
    console.log("Using fallback bilinear interpolation transform");
    
    // Use bilinear interpolation for transformation
    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        // Calculate normalized coordinates (0-1)
        const u = x / targetWidth;
        const v = y / targetHeight;

        // Use bilinear interpolation to find source coordinates
        const sourceCoord = this.bilinearInterpolation(sourcePoints, u, v);

        if (sourceCoord.x >= 0 && sourceCoord.x < sourceImage.width &&
            sourceCoord.y >= 0 && sourceCoord.y < sourceImage.height) {
          
          // Get pixel color from source using bilinear sampling
          const color = this.samplePixelBilinear(sourceImageData, sourceCoord.x, sourceCoord.y);
          
          // Set pixel in target
          const targetIndex = (y * targetWidth + x) * 4;
          imageData.data[targetIndex] = color.r;
          imageData.data[targetIndex + 1] = color.g;
          imageData.data[targetIndex + 2] = color.b;
          imageData.data[targetIndex + 3] = 255; // Alpha
        }
      }
    }

    targetCtx.putImageData(imageData, 0, 0);
    console.log("Fallback transformation completed");
  }



  bilinearInterpolation(corners, u, v) {
    // corners: [top-left, top-right, bottom-right, bottom-left]
    // u, v: normalized coordinates (0-1)
    
    const topLeft = corners[0];
    const topRight = corners[1];
    const bottomRight = corners[2];
    const bottomLeft = corners[3];

    // Interpolate along top edge
    const top = {
      x: topLeft.x + u * (topRight.x - topLeft.x),
      y: topLeft.y + u * (topRight.y - topLeft.y)
    };

    // Interpolate along bottom edge
    const bottom = {
      x: bottomLeft.x + u * (bottomRight.x - bottomLeft.x),
      y: bottomLeft.y + u * (bottomRight.y - bottomLeft.y)
    };

    // Interpolate between top and bottom
    return {
      x: top.x + v * (bottom.x - top.x),
      y: top.y + v * (bottom.y - top.y)
    };
  }



  samplePixelBilinear(imageData, x, y) {
    const { data, width, height } = imageData;
    
    // Clamp coordinates
    x = Math.max(0, Math.min(width - 1, x));
    y = Math.max(0, Math.min(height - 1, y));

    const x1 = Math.floor(x);
    const y1 = Math.floor(y);
    const x2 = Math.min(x1 + 1, width - 1);
    const y2 = Math.min(y1 + 1, height - 1);

    const fx = x - x1;
    const fy = y - y1;

    // Get pixel values at four corners
    const getPixel = (px, py) => {
      const idx = (py * width + px) * 4;
      return {
        r: data[idx],
        g: data[idx + 1],
        b: data[idx + 2]
      };
    };

    const p1 = getPixel(x1, y1);
    const p2 = getPixel(x2, y1);
    const p3 = getPixel(x1, y2);
    const p4 = getPixel(x2, y2);

    // Bilinear interpolation
    return {
      r: Math.round(p1.r * (1 - fx) * (1 - fy) + p2.r * fx * (1 - fy) + p3.r * (1 - fx) * fy + p4.r * fx * fy),
      g: Math.round(p1.g * (1 - fx) * (1 - fy) + p2.g * fx * (1 - fy) + p3.g * (1 - fx) * fy + p4.g * fx * fy),
      b: Math.round(p1.b * (1 - fx) * (1 - fy) + p2.b * fx * (1 - fy) + p3.b * (1 - fx) * fy + p4.b * fx * fy)
    };
  }

  enableControls() {
    if (this.submitButtonTarget) this.submitButtonTarget.disabled = false;
    if (this.resetButtonTarget) this.resetButtonTarget.disabled = false;
    if (this.autoDetectButtonTarget) this.autoDetectButtonTarget.disabled = false;
  }

  disableControls() {
    if (this.submitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.resetButtonTarget) this.resetButtonTarget.disabled = true;
    if (this.autoDetectButtonTarget) this.autoDetectButtonTarget.disabled = true;
  }

  showLoadingState(message = "Processing...") {
    const container = this.canvasContainerTarget;
    const overlay = document.createElement("div");
    overlay.className = "loading-overlay";
    overlay.innerHTML = `
      <div style="background: rgba(0,0,0,0.8); color: white; padding: 20px; border-radius: 8px; text-align: center;">
        <div>${message}</div>
        <div style="margin-top: 10px; width: 40px; height: 40px; border: 3px solid #fff; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 10px auto;"></div>
      </div>
    `;
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    `;
    container.style.position = "relative";
    container.appendChild(overlay);
  }

  hideLoadingState() {
    const overlay = this.canvasContainerTarget.querySelector(".loading-overlay");
    if (overlay) {
      overlay.remove();
    }
  }

  showSuccessMessage(message) {
    // Create a temporary success message
    const container = this.canvasContainerTarget;
    const successDiv = document.createElement("div");
    successDiv.className = "success-message";
    successDiv.innerHTML = `
      <div style="background: rgba(34, 197, 94, 0.9); color: white; padding: 12px 20px; border-radius: 6px; text-align: center; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; margin-right: 8px; vertical-align: text-top;">
          <path d="M9 12l2 2 4-4"/>
          <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.35 0 4.49.9 6.11 2.39"/>
        </svg>
        ${message}
      </div>
    `;
    successDiv.style.cssText = `
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 20;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    container.style.position = "relative";
    container.appendChild(successDiv);
    
    // Animate in
    requestAnimationFrame(() => {
      successDiv.style.opacity = "1";
    });
    
    // Remove after 3 seconds
    setTimeout(() => {
      successDiv.style.opacity = "0";
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.remove();
        }
      }, 300);
    }, 3000);
  }

  cancel() {
    this.hide();
  }

  handleResize() {
    // Debounce resize events
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      if (this.modalTarget.style.display !== "none" && this.canvas) {
        // Update mobile class
        if (window.innerWidth <= 768) {
          this.modalTarget.classList.add('perspective-crop-modal--mobile');
        } else {
          this.modalTarget.classList.remove('perspective-crop-modal--mobile');
        }
        
        // Reinitialize canvas with new dimensions
        const currentPoints = [...this.points];
        this.initializeCanvas();
        
        // Restore points if they exist
        if (currentPoints.length === 4) {
          this.points = currentPoints;
          this.draw();
        }
      }
    }, 250);
  }

  overlayClick(event) {
    if (event.target === this.modalTarget) {
      this.cancel();
    }
  }
}